import React from 'react';
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  useMediaQuery
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import auth from 'services/auth';
import LocalStorageService from 'services/LocalStorageService';
import NotificationMenu from 'components/common/NotificationMenu';
import useConfiguration from 'hooks/useConfiguration';
import { adminTokens } from 'theme/adminTokens';
import AdminSidebar from 'components/layout/AdminSidebar';
import { crumbLabel, initialsFromName } from 'components/layout/navigation';

const AdminRoute = () => {
  const mobile = useMediaQuery('(max-width:900px)');
  const narrow = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = React.useRef<HTMLInputElement>(null);
  const [search, setSearch] = React.useState('');
  const [userMenu, setUserMenu] = React.useState<null | HTMLElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const enableNotifications = useConfiguration<boolean>('enable_notifications');
  const displayName = LocalStorageService.getUserName();
  const drawerWidth = mobile ? 0 : adminTokens.sidebar;

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (!auth.isAuthenticated()) {
    return <Navigate to="/login" />;
  }

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const query = search.trim();
    navigate(query ? `/my_events?q=${encodeURIComponent(query)}` : '/my_events');
  };

  const closeMenu = () => setMenuOpen(false);

  const sidebarBody = (compact: boolean) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <AdminSidebar compact={compact} onNavigate={closeMenu} />
      <Box sx={{ flexShrink: 0, pt: 1, background: adminTokens.paper }}>
        <Box
          onClick={(event) => setUserMenu(event.currentTarget)}
          sx={{
            borderTop: compact ? 0 : `1px solid ${adminTokens.line}`,
            pt: compact ? 0 : '22px',
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            justifyContent: compact ? 'center' : 'flex-start',
            cursor: 'pointer'
          }}
        >
          <Box
            sx={{
              borderRadius: '50%',
              width: 34,
              height: 34,
              background: '#202c28',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: 14,
              fontWeight: 700,
              flexShrink: 0
            }}
          >
            {initialsFromName(displayName)}
          </Box>
          {!compact && (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName || 'Account'}</Box>
              <Box sx={{ fontSize: 13, color: adminTokens.muted, lineHeight: 1.7 }}>
                {auth.isAdmin() ? 'Workspace admin' : 'Member'}
              </Box>
            </Box>
          )}
        </Box>
        <Menu anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)}>
          <MenuItem disabled>{displayName || 'Account'}</MenuItem>
          <MenuItem
            onClick={() => {
              setUserMenu(null);
              closeMenu();
              navigate('/logout');
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: adminTokens.canvas, color: adminTokens.ink }}>
      <CssBaseline />
      {mobile ? (
        <Drawer
          variant="temporary"
          open={menuOpen}
          onClose={closeMenu}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              padding: '24px 18px',
              background: adminTokens.paper,
              borderRight: `1px solid ${adminTokens.line}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
          {sidebarBody(false)}
        </Drawer>
      ) : (
        <Box
          component="aside"
          sx={{
            position: 'fixed',
            width: drawerWidth,
            top: 0,
            bottom: 0,
            left: 0,
            background: adminTokens.paper,
            borderRight: `1px solid ${adminTokens.line}`,
            padding: '30px 18px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1200,
            overflow: 'hidden'
          }}
        >
          {sidebarBody(false)}
        </Box>
      )}

      <Box component="main" sx={{ marginLeft: `${drawerWidth}px`, flex: 1, minWidth: 0, minHeight: '100vh' }}>
        <Box
          component="header"
          sx={{
            height: narrow ? 60 : 77,
            background: '#fff',
            borderBottom: `1px solid ${adminTokens.line}`,
            px: narrow ? 2 : '38px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}
        >
          {mobile && (
            <IconButton aria-label="Open menu" onClick={() => setMenuOpen(true)} sx={{ color: adminTokens.ink, ml: -0.5 }}>
              <MenuIcon />
            </IconButton>
          )}
          {!narrow && (
            <Box sx={{ color: '#8d9591', fontSize: 14, whiteSpace: 'nowrap' }}>
              Workspace
              <Box component="span" sx={{ mx: '12px' }}>
                /
              </Box>
              <Box component="span" sx={{ color: '#3a4641', fontWeight: 500 }}>
                {crumbLabel(location.pathname)}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: narrow ? 1 : 3, ml: mobile ? 0 : 'auto', minWidth: 0, flex: mobile ? 1 : '0 1 auto' }}>
            <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: mobile ? 1 : undefined, color: adminTokens.muted }}>
              <Box component="span" aria-hidden="true">
                ⌕
              </Box>
              <InputBase
                inputRef={searchRef}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={narrow ? 'Search events' : 'Search events...'}
                inputProps={{ 'aria-label': 'Search events' }}
                sx={{ width: mobile ? '100%' : 235, fontSize: 14 }}
              />
            </Box>
            {enableNotifications && <NotificationMenu size={narrow ? 'small' : 'medium'} />}
          </Box>
        </Box>
        <Box
          sx={{
            maxWidth: 1600,
            margin: 'auto',
            padding: narrow ? '22px 15px' : '34px 38px'
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminRoute;
